import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { GridCloseIcon } from "@mui/x-data-grid";
import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { ReactComponent as EditIcon } from "../../assets/images/edit-icon.svg";
import { ReactComponent as ToggleBarIcon } from "../../assets/images/toggle-arrow.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/trash-icon.svg";
import { ReactComponent as CriticalIcon } from "../../assets/images/critical-icon.svg";
import CButton from "../../components/CButton";
import CDataGrid from "../../components/CDataGrid";
import CDialog from "../../components/CDialog";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import CSwitchToggle from "../../components/CSwitchToggle";
import CTextField from "../../components/CTextField";
import { supabase } from "../../config/supabaseClient";
import { messages } from "../../constant/alertMessages";
import {
  COST_PER_LEARNER_LIMIT,
  NQF_ALIGNED_OPTIONS,
  NQF_LEVEL_OPTIONS,
  PROGRAM_DURATION_OPTIONS,
  TRAINING_CATALOGUE_FIELDS,
  isCatalogueAvailable,
  roleType,
  statuses,
} from "../../constant/constants";
import { TRAINING_CATALOGUE_PLACEHOLDERS } from "../../constant/placeholders";
import {
  downloadExcelTemplateUtility,
  formatZAR,
} from "../../constant/utils/commonUtils";
import { isCompanyPrivileged } from "../../utils/roleUtils";
import {
  addTrainingCatalogue,
  fetchTrainingCatalogues,
  setCurrentIFundiCategory,
  updateTrainingCatalogueStatus,
} from "../../slices/trainingCatalogueSlice";
import {
  addTrainingIsCriticalLog,
  updateTrainingIsCriticalLog,
} from "../../slices/trainingTypeSlice";
import {
  batchSyncTrainingStatusToVectorDB,
  deleteTrainingSelectionsByTrainingId,
  fetchTrainingIdsForCatalogue,
  getAllocatedTrainingIds,
} from "../../utils/trainingAvailabilityUtils";

const IFundiCatalogue = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const localUser = JSON.parse(localStorage.getItem("sdfUser"));
  const userRole = localUser?.role;
  const dispatch = useDispatch();
  const { categories } = useSelector((state) => state.trainingCatalogue);
  const navigate = useNavigate();
  const location = useLocation();
  const [dialogState, setDialogState] = useState({ id: null, show: false });
  const [alertDialogState, setAlertDialogState] = useState({
    id: null,
    show: false,
  });
  const [trainingName, setTrainingName] = useState("");
  const [excelData, setExcelData] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [selectedIFundiCategory, setSelectedIFundiCategory] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);
  const { currentIFundiCategory } = useSelector(
    (state) => state.trainingCatalogue
  );
  const [isCompanyOpen, setIsCompanyOpen] = useState(true);
  const [courseDetailsDialog, setCourseDetailsDialog] = useState({
    open: false,
    description: "",
    title: "",
  });
  const [validationDialog, setValidationDialog] = useState({
    show: false,
    errors: [],
  });
  const [trainingStatusWarningDialog, setTrainingStatusWarningDialog] = useState({
    show: false,
    trainingId: null,
    trainingName: "",
  });
  const [catalogueStatusWarningDialog, setCatalogueStatusWarningDialog] =
    useState({
      show: false,
      categoryId: null,
      nextCatalogueActive: null,
    });

  const companyId = localUser?.company_id;

  // Validate required fields for each row
  const validateRowData = (jsonData) => {
    const errors = [];
    // Get required fields from constants - Cost per Learner, Provider, Program Duration
    const requiredFieldNames = ["Cost per Learner", "Provider", "Program Duration"];
    const requiredFields = TRAINING_CATALOGUE_FIELDS.filter((field) =>
      requiredFieldNames.includes(field.headerName)
    ).map((field) => ({
      headerName: field.headerName,
      fieldKey: field.headerName,
      isNumber: field.headerName === "Cost per Learner",
    }));

    jsonData.forEach((row, index) => {
      const rowNumber = index + 2;
      const rowErrors = [];

      requiredFields.forEach((field) => {
        const value = row[field.fieldKey];
        const isEmpty =
          value === null ||
          value === undefined ||
          value === "" ||
          (typeof value === "string" && value.trim() === "");

        if (isEmpty) {
          rowErrors.push(`${field.headerName} is required`);
        } else if (field.isNumber) {
          // Validate that Cost per Learner is a valid number
          const numericValue = typeof value === "string" 
            ? parseFloat(value.toString().replace(/[^0-9.-]+/g, ""))
            : parseFloat(value);
          
          if (isNaN(numericValue) || numericValue < 0) {
            rowErrors.push(`${field.headerName} must be a valid positive number`);
          }
        }
      });

      if (rowErrors.length > 0) {
        errors.push({
          row: rowNumber,
          errors: rowErrors,
        });
      }
    });

    return errors;
  };

  // Helper function to update training in vector database (Qdrant)
  const updateTrainingInVectorDB = async (trainingId, updates) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/importTrainingCatalogue`,
        {
          trainingId: trainingId,
          updates: updates,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${process.env.REACT_APP_BACKEND_API_KEY}`,
          },
        }
      );

      if (!response.data?.success) {
        console.warn(
          "Vector database update failed:",
          response.data?.message || response.data?.error
        );
        return {
          success: false,
          error: response.data?.message || response.data?.error,
        };
      }

      return response.data;
    } catch (error) {
      console.warn(
        "Vector database update failed (non-critical):",
        error.message || error
      );
      return { success: false, error: error.message || "Vector update failed" };
    }
  };

  // Create base columns
  let baseColumns = [];

  baseColumns.push({
    field: "training_name",
    headerName: "Training Name",
    minWidth: 180,
    flex: 1,
  });

  baseColumns.push({
    field: "nqf_level",
    headerName: "NQF Level",
    minWidth: 120,
    renderCell: (params) => (
      <span>
        {params.row.nqf_aligned === "Yes" ? params.row.nqf_level || "-" : "-"}
      </span>
    ),
  });

  baseColumns.push({
    field: "program_duration",
    headerName: "Program Duration",
    minWidth: 180,
    sortable: false,
    renderCell: (params) => <span>{params.row.program_duration || "-"}</span>,
  });

  TRAINING_CATALOGUE_FIELDS.forEach((field) => {
    if (field.field === "training_type") {
      return;
    }
    if (
      field.field !== "training_name" &&
      field.field !== "nqf_level" &&
      field.field !== "program_duration"
    ) {
      if (
        field.field === "training_expense" ||
        field.field === "cost" ||
        field.headerName === "Cost per Learner"
      ) {
        baseColumns.push({
          field: field.field,
          headerName: field.headerName,
          minWidth: 180,
          renderCell: (params) => <span>{formatZAR(params.value)}</span>,
        });
      } else if (field.field === "training_description") {
        // Add clickable Training Details with popup
        baseColumns.push({
          field: field.field,
          headerName: field.headerName,
          minWidth: field.minWidth || 210,
          flex: 1,
          sortable: false,
          renderCell: (params) => {
            const description = params.value || "";
            const truncatedDescription =
              description.length > 100
                ? description.substring(0, 100) + "..."
                : description;

            const handleClick = () => {
              if (description) {
                setCourseDetailsDialog({
                  open: true,
                  description: description,
                  title: params.row.training_name || "Training Details",
                });
              }
            };

            return (
              <span
                className={
                  description ? "cursor-pointer hover:text-blue-500 " : ""
                }
                onClick={handleClick}
                title="Click to view full description"
              >
                {truncatedDescription}
              </span>
            );
          },
        });
      } else if (field.field === "training_url") {
        // Add clickable Training URL
        baseColumns.push({
          field: field.field,
          headerName: field.headerName,
          minWidth: 180,
          sortable: false,
          renderCell: (params) => {
            const url = params.value || "";
            if (url) {
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700 underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Link
                </a>
              );
            }
            return <span>-</span>;
          },
        });
      } else {
        baseColumns.push({
          field: field.field,
          headerName: field.headerName,
          minWidth: 210,
          sortable: false,
        });
      }
    }
  });

  const columns = [
    ...baseColumns,
    ...(userRole === roleType.ADMIN
      ? [
          {
            field: "availability",
            headerName: "Availability",
            width: 100,
            sortable: false,
            renderCell: (params) => {
              const isActive = params.row.training_status === statuses.ACTIVE;
              const catalogueOn = isCatalogueAvailable(selectedIFundiCategory);
              const isAllocated = params.row.is_allocated === true;

              return (
                <div className="flex items-center h-full">
                  <CSwitchToggle
                    className="progress-switch-btn"
                    checked={isActive}
                    disabled={!catalogueOn}
                    onChange={() =>
                      handleTrainingStatusToggle(
                        params.row.training_id,
                        params.row.training_name,
                        isActive,
                        isAllocated
                      )
                    }
                  />
                </div>
              );
            },
          },
        ]
      : []),
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => {
        return (
          <div className="flex 2xl:gap-3 gap-2 items-center h-full">
            {/* Only show critical icon for SDF users, not for admin */}
            {isCompanyPrivileged(userRole) &&
              (() => {
                const isCritical = params.row.is_critical || false;
                const iconColor = isCritical ? "#FF383C" : "#008CD1";
                const uniqueId = `critical-icon-${params.row.training_id}-${isCritical}`;

                return (
                  <CButton
                    tooltip={
                      isCritical ? "Mark as Not Critical" : "Mark as Critical"
                    }
                    tooltipPosition={"top"}
                    onClick={() =>
                      handleIsCriticalToggleIFundi(
                        params.row.training_id,
                        !isCritical
                      )
                    }
                    className="!min-w-0 bg-transparent hover:bg-gray-100 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
                  >
                    <div
                      id={uniqueId}
                      className="2xl:!w-[24px] 2xl:!h-[24px] !w-[20px] !h-[20px] critical-icon-wrapper"
                    >
                      <CriticalIcon className="w-full h-full critical-icon-svg" />
                      <style
                        dangerouslySetInnerHTML={{
                          __html: `
                      #${uniqueId} svg path {
                        stroke: ${iconColor} !important;
                      }
                    `,
                        }}
                      />
                    </div>
                  </CButton>
                );
              })()}
            {userRole === roleType.ADMIN && (
              <>
                <CButton
                  tooltip="Edit"
                  tooltipPosition={"top"}
                  onClick={() => handleEditTraining(params.row.training_id)}
                  className="!min-w-0 bg-blue-100 text-blue-600 hover:bg-blue-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
                >
                  <EditIcon className="!fill-blue-500 !text-blue-500" />
                </CButton>
                <CButton
                  tooltip="Delete"
                  tooltipPosition={"top"}
                  onClick={() => {
                    setAlertDialogState({
                      id: params.row.training_id,
                      show: true,
                    });
                  }}
                  className="!min-w-0 bg-red-100 text-blue-600 hover:bg-red-200 2xl:!h-[28px] 2xl:!w-[28px] !h-[24px] !w-[24px] !p-0 !rounded-[5px] inline-flex items-center justify-center"
                >
                  <DeleteIcon className="!fill-red-500 !text-red-500" />
                </CButton>
              </>
            )}
          </div>
        );
      },
    },
  ];

  // Add effect to handle grid refresh
  useEffect(() => {
    if (location.state?.refreshGrid && location.state?.categoryId) {
      const category = categories.find(
        (cat) => cat.id === location.state.categoryId
      );
      if (category) {
        setSelectedIFundiCategory(category);
        fetchTrainingData(category.id);
      }
      navigate(location.pathname, { replace: true });
    }
  }, [
    location.state?.refreshGrid,
    location.state?.categoryId,
    categories,
    navigate,
    location.pathname,
  ]);

  // Filter categories for IFundi Catalogue
  useEffect(() => {
    let filtered = categories.filter((cat) => cat.company_id === null);

    // For SDF users, only show active categories
    if (isCompanyPrivileged(userRole)) {
      filtered = filtered.filter((cat) => cat.status === true);
    }
    // Admin users can see all categories (active and inactive)

    setFilteredCategories(filtered);
    setGridData([]);

    if (filtered && filtered.length > 0) {
      // Sort the filtered array to match the render order
      const sortedFiltered = [...filtered].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          sensitivity: "base",
        })
      );

      // First, try to maintain the currently selected category if it still exists in the filtered list
      if (selectedIFundiCategory && selectedIFundiCategory.id) {
        const stillExists = sortedFiltered.find(
          (cat) => cat.id === selectedIFundiCategory.id
        );
        if (stillExists) {
          // Category still exists, keep it selected and update the index
          const newIndex = sortedFiltered.findIndex(
            (cat) => cat.id === selectedIFundiCategory.id
          );
          if (newIndex !== -1) {
            dispatch(setCurrentIFundiCategory(newIndex));
          }
          setSelectedIFundiCategory(stillExists);
          fetchTrainingData(stillExists.id);
          return;
        }
      }

      // If no valid selected category, use index-based selection
      const categoryIndex =
        currentIFundiCategory !== null &&
        currentIFundiCategory !== undefined &&
        currentIFundiCategory >= 0 &&
        currentIFundiCategory < sortedFiltered.length
          ? currentIFundiCategory
          : 0;
      const selectedCategory = sortedFiltered[categoryIndex];
      if (selectedCategory && selectedCategory.id) {
        setSelectedIFundiCategory(selectedCategory);
        fetchTrainingData(selectedCategory.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, userRole, currentIFundiCategory]);

  // Fetch categories when component mounts
  useEffect(() => {
    const fetchCatalogues = async () => {
      try {
        const result = await dispatch(fetchTrainingCatalogues(null));
        if (result.error) {
          console.error("Error in fetch result:", result.error);
          toast.error("Error fetching training categories");
        }
      } catch (error) {
        console.error("Error fetching initial categories:", error);
        toast.error("Error fetching training categories");
      }
    };

    fetchCatalogues();
  }, [dispatch]);

  const handleDelete = async (trainingId) => {
    try {
      const { data: existingData, error: checkError } = await supabase
        .from("training_catalogue_data")
        .select("*")
        .eq("id", trainingId)
        .single();

      if (checkError) throw checkError;

      if (existingData) {
        await deleteTrainingSelectionsByTrainingId(supabase, trainingId);

        const { error: updateError } = await supabase
          .from("training_catalogue_data")
          .update({
            status: "archived",
          })
          .eq("id", trainingId);

        if (updateError) throw updateError;

        toast.success("Training archived successfully");
        fetchTrainingData(selectedIFundiCategory?.id);
      } else {
        throw new Error("Training not found");
      }
    } catch (error) {
      console.error("Error archiving training:", error);
      toast.error(error.message || "Error archiving training");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    try {
      if (!file) {
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

          // Reject blank Excel files (no data rows)
          if (!rows.length || jsonData.length === 0) {
            toast.error("Cannot upload blank file. Please upload a file with data.");
            resetFileInput();
            return;
          }

          const headers = rows[0].map((h) => (h ?? "").toString().trim());

          // Get all fields except Training URL (optional) and NQF Level (conditionally required)
          let allRequiredFields = TRAINING_CATALOGUE_FIELDS.filter(
            (field) => field.field !== "training_url" && field.field !== "nqf_level"
          ).map((field) => field.headerName);
          // No "Is Critical" field for IFundi Catalogue

          const missingFields = allRequiredFields.filter(
            (field) => !headers.includes(field)
          );

          if (missingFields.length > 0) {
            toast.error(`Missing required fields: ${missingFields.join(", ")}`);
            resetFileInput();
            return;
          }

          const processedData = jsonData.map((row, index) => {
            const nqfAlignedVal = row["NQF Aligned"];
            let normalizedNqfAligned = false;

            if (
              nqfAlignedVal !== undefined &&
              nqfAlignedVal !== null &&
              nqfAlignedVal !== ""
            ) {
              const strVal = nqfAlignedVal.toString().trim().toLowerCase();

              if (["true", "yes", "1"].includes(strVal)) {
                normalizedNqfAligned = true;
              } else if (["false", "no", "0"].includes(strVal)) {
                normalizedNqfAligned = false;
              } else {
                const validNqfAlignedValues = NQF_ALIGNED_OPTIONS.map(
                  (option) => option.value
                );
                throw new Error(
                  `Invalid NQF Aligned value "${nqfAlignedVal}" in row ${
                    index + 2
                  }. Valid options: ${validNqfAlignedValues.join(", ")}`
                );
              }
            }

            const processedRow = {
              ...row,
              "NQF Aligned": normalizedNqfAligned,
            };

            if (!normalizedNqfAligned) {
              processedRow["NQF Level"] = "";
            }

            return processedRow;
          });

          const missingNqfLevelRow = processedData.findIndex((row) => {
            const nqfAligned = row["NQF Aligned"];
            const nqfLevel = row["NQF Level"];

            if (nqfAligned) {
              return !nqfLevel || nqfLevel.toString().trim() === "";
            }
            return false;
          });

          if (missingNqfLevelRow !== -1) {
            toast.error(
              `NQF Level is mandatory when NQF Aligned is "yes" in row ${
                missingNqfLevelRow + 2
              }`
            );
            resetFileInput();
            return;
          }

          const validNqfLevelValues = NQF_LEVEL_OPTIONS.map(
            (option) => option.value
          );
          const invalidNqfLevelRows = processedData
            .map((row, index) => {
              const nqfLevel = row["NQF Level"];
              const nqfAligned = row["NQF Aligned"];

              if (
                nqfAligned === true &&
                nqfLevel &&
                nqfLevel.toString().trim() !== "" &&
                !validNqfLevelValues.includes(nqfLevel.toString().trim())
              ) {
                return { row: index + 2, value: nqfLevel };
              }
              return null;
            })
            .filter((item) => item !== null);

          if (invalidNqfLevelRows.length > 0) {
            const errorMessage = `Invalid NQF Level value "${
              invalidNqfLevelRows[0].value
            }" in row ${
              invalidNqfLevelRows[0].row
            }. Valid options: ${validNqfLevelValues.join(", ")}`;
            toast.error(errorMessage);
            resetFileInput();
            return;
          }

          setExcelData(processedData);
          setSelectedFileName(file.name);
        } catch (error) {
          console.error("Error processing Excel file:", error);
          toast.error(error.message);
          resetFileInput();
        }
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        toast.error("Error reading file");
        resetFileInput();
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error in handleFileUpload:", error);
      toast.error("Error uploading file");
      resetFileInput();
    }
  };

  const downloadTrainingCatalogueTemplate = async () => {
    const templateColumns = [...TRAINING_CATALOGUE_FIELDS];
    // No "Is Critical" field for IFundi Catalogue template

    const dropdownConfigs = [
      {
        columnName: "NQF Aligned",
        options: NQF_ALIGNED_OPTIONS.map((v) => v.value),
      },
      {
        columnName: "NQF Level",
        options: NQF_LEVEL_OPTIONS.map((v) => v.value),
      },
      {
        columnName: "Program Duration",
        options: PROGRAM_DURATION_OPTIONS.map((v) => v.value),
      },
    ];

    await downloadExcelTemplateUtility({
      fileName: "Training_Catalogue_Template.xlsx",
      sheetName: "Training Catalogue",
      columns: templateColumns,
      dropdownConfigs: dropdownConfigs,
      getHeader: (col) => col.headerName,
      context: { activeTab: "IFundiCatalogue", userRole, roleType },
      dataValidationStartRow: 2,
      dataValidationEndRow: 100,
    });
  };

  const handleSave = async () => {
    // Prevent multiple submissions
    if (isSaving) {
      return;
    }

    if (!trainingName.trim()) {
      toast.error("Please enter a training name");
      return;
    }

    // Validate required fields for each row only if there are rows
    if (excelData && excelData.length > 0) {
      const validationErrors = validateRowData(excelData);
      if (validationErrors.length > 0) {
        // Collect all error messages like organogram does
        const errorMessages = validationErrors.map((error) => {
          const errorList = error.errors.join(", ");
          return `Row ${error.row}: ${errorList}`;
        });
        // Close the Add Catalogue dialog
        setDialogState({ id: null, show: false });
        // Show validation error dialog
        setValidationDialog({
          show: true,
          errors: errorMessages,
        });
        return;
      }
    }

    setIsSaving(true);

    let trainingDataToInsert = null;

    try {
      if (excelData && excelData.length > 0) {
        trainingDataToInsert = excelData.map((item, index) => {
          const mappedData = {
            company_id: null,
            catalogue_id: null,
            name: item["Training Name"] || "",
            type: null,
            training_category: null,
            cost: item["Cost per Learner"]
              ? parseFloat(
                  item["Cost per Learner"].toString().replace(/[^0-9.-]+/g, "")
                )
              : 0,
            provider: item["Provider"] || "",
            description:
              item["Training Details"] ||
              item["Course Details"] ||
              item["Description"] ||
              null,
            targeted_skills: item["Targeted Skills"] || null,
            program_duration: item["Program Duration"] || null,
            nqf_aligned: item["NQF Aligned"] || null,
            nqf_level: item["NQF Level"] || null,
            url: item["Training URL"] || null,
            is_critical: false,
            status: "active",
          };
          return mappedData;
        });

        for (let i = 0; i < trainingDataToInsert.length; i++) {
          const item = trainingDataToInsert[i];
          const cost = item.cost;
          if (cost > COST_PER_LEARNER_LIMIT) {
            toast.error(
              `Row ${i + 1}: ${messages.COST_PER_LEARNER_LIMIT_MESSAGE(COST_PER_LEARNER_LIMIT)}`
            );
            return;
          }
        }
      }

      const categoryData = {
        name: trainingName,
        company_id: null,
        created_by: localUser.id,
      };
      const result = await dispatch(addTrainingCatalogue(categoryData));
      if (result.payload.error) {
        toast.error(result.payload.error);
        return;
      }

      const savedCategory = result.payload || result.payload;

      if (!savedCategory || !savedCategory.id) {
        console.error("Invalid saved category response:", result);
        throw new Error("Failed to get category ID after saving");
      }

      if (excelData && excelData.length > 0) {
        trainingDataToInsert.forEach((item) => {
          item.catalogue_id = savedCategory.id;
        });

        const { data: insertedData, error: insertError } = await supabase
          .from("training_catalogue_data")
          .insert(trainingDataToInsert)
          .select();

        if (insertError) {
          console.error("Error inserting training data:", insertError);
          throw insertError;
        }

        const mappedGridData = insertedData.map((item, index) => ({
          id: index + 1,
          training_id: item.id,
          company_id: item.company_id,
          training_name: item.name,
          training_expense: item.cost,
          training_provider: item.provider,
          training_description: item.description,
          targeted_skills: item.targeted_skills,
          nqf_aligned: item.nqf_aligned,
          nqf_level: item.nqf_level,
          is_critical: item.is_critical || false,
          program_duration: item.program_duration,
          training_status: item.status || statuses.ACTIVE,
          is_allocated: false,
        }));

        setGridData(mappedGridData);
      }

      setTrainingName("");
      setExcelData(null);
      setDialogState({ id: null, show: false });
      setSelectedFileName("");
      dispatch(setCurrentIFundiCategory(0));
      toast.success("Training category and data saved successfully!");

      await dispatch(fetchTrainingCatalogues(null));
    } catch (error) {
      console.error("Error saving training category:", error);
      let errorMessage = "Error saving training category";

      const errorPayload = error?.payload || error;

      if (errorPayload?.code === "23505") {
        errorMessage =
          "Training name already exists. Please use a different name.";
      } else if (errorPayload?.message) {
        errorMessage =
          "Training name already exists. Please use a different name";
      }

      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const fetchTrainingData = async (categoryId) => {
    try {
      const { data, error } = await supabase
        .from("training_catalogue_data")
        .select("*")
        .eq("catalogue_id", categoryId)
        .neq("status", "archived");

      if (error) throw error;

      let mappedData = data.map((item, index) => ({
        id: index + 1,
        training_id: item.id,
        company_id: item.company_id,
        training_name: item.name,
        training_expense: item.cost,
        training_provider: item.provider,
        program_duration: item.program_duration,
        training_description: item.description,
        targeted_skills: item.targeted_skills,
        training_url: item.url || "",
        nqf_aligned: item.nqf_aligned ? "Yes" : "No",
        nqf_level: item.nqf_aligned ? item.nqf_level : "-",
        is_critical: item.is_critical || false,
        training_status: item.status || statuses.ACTIVE,
        is_allocated: false,
        short_course_type_name: null,
      }));

      // Check which trainings are already allocated
      if (mappedData.length > 0) {
        const trainingIds = mappedData.map((row) => row.training_id);
        
        // Parallel fetch for types and allocations
        const [typeResult, allocResult] = await Promise.all([
          supabase
            .from("training_catalogue_data_types")
            .select("training_id, type, is_critical")
            .in("training_id", trainingIds),
          supabase
            .from("selected_trainings")
            .select("training_id")
            .in("training_id", trainingIds)
        ]);

        const typeData = typeResult.data;
        const allocations = allocResult.data;

        const typeMap = {};
        const isCriticalMap = {};
        if (typeData) {
          typeData.forEach((row) => {
            typeMap[row.training_id] = row.type;
            isCriticalMap[row.training_id] = row.is_critical || false;
          });
        }

        const allocatedSet = new Set(allocations?.map((a) => a.training_id) || []);

        mappedData = mappedData.map((row) => ({
          ...row,
          short_course_type_name: typeMap[row.training_id] || null,
          is_allocated: allocatedSet.has(row.training_id),
          is_critical:
            isCriticalMap[row.training_id] !== undefined
              ? isCriticalMap[row.training_id]
              : row.is_critical || false,
        }));
      }

      setGridData(mappedData);
    } catch (error) {
      console.error("Error fetching training data:", error);
      toast.error("Error fetching training data");
    }
  };

  const handleIFundiCategoryClick = (category, index) => {
    if (category) {
      dispatch(setCurrentIFundiCategory(index));
      setSelectedIFundiCategory(category);
      fetchTrainingData(category.id);
    }
  };

  const handleDialogClose = () => {
    setDialogState({ id: null, show: false });
    setTrainingName("");
    setExcelData(null);
    setSelectedFileName("");

    if (!selectedIFundiCategory) {
      setGridData([]);
    }
  };

  const handleExport = () => {
    try {
      if (!gridData.length) {
        toast.warning("No data to export");
        return;
      }

      const wb = XLSX.utils.book_new();
      const wsData = gridData.map((row) => {
        // Ensure is_critical is properly handled - check for boolean true, string "Yes", or number 1
        const isCritical = row.hasOwnProperty("is_critical")
          ? row.is_critical === true ||
            row.is_critical === "Yes" ||
            row.is_critical === 1 ||
            row.is_critical === "TRUE"
          : false;

        const rowData = {
          "Training Name": row.training_name || "",
          "Program Duration": row.program_duration || "",
          "Cost per Learner": row.training_expense || 0,
          Provider: row.training_provider || "",
          "Training Details": row.training_description || "",
          "Targeted Skills": row.targeted_skills || "",
          "NQF Aligned": row.nqf_aligned || "",
          "NQF Level": row.nqf_level || "",
          "Training URL": row.training_url || "", // training_url is the grid field name, url is the DB field
        };

        // Only include "Is Critical?" field for SDF/company_admin users, not for admin
        if (isCompanyPrivileged(userRole)) {
          rowData["Is Critical?"] = isCritical ? "Yes" : "No";
        }

        return rowData;
      });

      const ws = XLSX.utils.json_to_sheet(wsData);
      XLSX.utils.book_append_sheet(wb, ws, "Training Catalogue");
      XLSX.writeFile(wb, "Training_Catalogue.xlsx");
      toast.success("Training catalogue exported successfully!");
    } catch (error) {
      console.error("Error exporting training catalogue:", error);
      toast.error("Error exporting training catalogue");
    }
  };

  const executeCatalogueStatusChange = async (categoryId, newCatalogueActive) => {
    await dispatch(
      updateTrainingCatalogueStatus({
        categoryId,
        status: newCatalogueActive,
      })
    ).unwrap();
    const trainingIds = await fetchTrainingIdsForCatalogue(supabase, categoryId);
    const vecStatus = newCatalogueActive ? statuses.ACTIVE : statuses.INACTIVE;
    await batchSyncTrainingStatusToVectorDB(trainingIds, vecStatus);
    if (selectedIFundiCategory?.id === categoryId) {
      await fetchTrainingData(categoryId);
    }
    toast.success("Catalogue availability updated successfully");
  };

  const handleStatusToggle = async (categoryId, currentStatus) => {
    const newCatalogueActive = !currentStatus;
    try {
      if (currentStatus === true && newCatalogueActive === false) {
        const trainingIds = await fetchTrainingIdsForCatalogue(
          supabase,
          categoryId
        );
        const allocated = await getAllocatedTrainingIds(supabase, trainingIds);
        if (allocated.length > 0) {
          setCatalogueStatusWarningDialog({
            show: true,
            categoryId,
            nextCatalogueActive: newCatalogueActive,
          });
          return;
        }
      }
      await executeCatalogueStatusChange(categoryId, newCatalogueActive);
    } catch (error) {
      console.error("Error updating catalogue status:", error);
      toast.error(error?.message || "Error updating catalogue status");
    }
  };

  const handleEditTraining = async (trainingId) => {
    try {
      const { data, error } = await supabase
        .from("training_catalogue_data")
        .select("*")
        .eq("id", trainingId)
        .single();

      if (error) throw error;

      if (data) {
        let nqfAlignedValue = "";
        if (data.nqf_aligned === true) nqfAlignedValue = "Yes";
        else if (data.nqf_aligned === false) nqfAlignedValue = "No";

        let programDurationId = "";
        if (data.program_duration) {
          let matchingOption = PROGRAM_DURATION_OPTIONS.find(
            (option) => option.value === data.program_duration
          );
          if (!matchingOption) {
            matchingOption = PROGRAM_DURATION_OPTIONS.find(
              (option) => option.id === data.program_duration
            );
          }
          programDurationId = matchingOption
            ? matchingOption.id
            : data.program_duration;
        }

        navigate("/add-training-catalogue", {
          state: {
            categoryId: data.catalogue_id,
            trainingId: data.id,
            editMode: true,
            activeTab: "IFundiCatalogue",
            trainingData: {
              trainingName: data.name || "",
              programDuration: programDurationId,
              targetedSkills: data.targeted_skills || "",
              trainingCostPerHead: data.cost ? data.cost.toString() : "",
              trainingProviderName: data.provider || "",
              trainingDescription: data.description || "",
              nqf_aligned: nqfAlignedValue,
              nqf_level: data.nqf_level || "",
              trainingUrl: data.url || "",
              is_critical: data.is_critical || false,
            },
          },
        });
      }
    } catch (error) {
      console.error("Error handling training data:", error);
      toast.error("Error handling training data");
    }
  };

  const handleCancelUpload = () => {
    setSelectedFileName("");
    setExcelData(null);
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const resetFileInput = () => {
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const applyTrainingRowStatus = async (trainingId, newStatus) => {
    const previousStatus =
      newStatus === statuses.ACTIVE ? statuses.INACTIVE : statuses.ACTIVE;

    setGridData((prevData) =>
      prevData.map((row) =>
        row.training_id === trainingId
          ? { ...row, training_status: newStatus }
          : row
      )
    );

    try {
      const { error: updateError } = await supabase
        .from("training_catalogue_data")
        .update({ status: newStatus })
        .eq("id", trainingId);

      if (updateError) {
        setGridData((prevData) =>
          prevData.map((row) =>
            row.training_id === trainingId
              ? { ...row, training_status: previousStatus }
              : row
          )
        );
        console.error("Error updating training status:", updateError);
        toast.error(`Failed to update training status: ${updateError.message}`);
        return;
      }

      toast.success(
        `Training ${
          newStatus === statuses.ACTIVE ? "available" : "unavailable"
        } successfully`
      );

      updateTrainingInVectorDB(trainingId, { status: newStatus }).then(
        (vectorResult) => {
          if (!vectorResult?.success) {
            console.warn(
              "Training status saved, but vector sync failed:",
              vectorResult?.error
            );
          }
        }
      );
    } catch (error) {
      setGridData((prevData) =>
        prevData.map((row) =>
          row.training_id === trainingId
            ? { ...row, training_status: previousStatus }
            : row
        )
      );
      console.error("Error updating training status:", error);
      toast.error("Error updating training status");
    }
  };

  const handleTrainingStatusToggle = async (
    trainingId,
    trainingName,
    currentlyActive,
    isAllocated
  ) => {
    if (!isCatalogueAvailable(selectedIFundiCategory)) {
      toast.warning(
        "Turn the catalogue on before changing individual training availability."
      );
      return;
    }

    const newStatus = currentlyActive ? statuses.INACTIVE : statuses.ACTIVE;

    if (currentlyActive && isAllocated) {
      setTrainingStatusWarningDialog({
        show: true,
        trainingId,
        trainingName,
      });
      return;
    }

    await applyTrainingRowStatus(trainingId, newStatus);
  };

  const handleIsCriticalToggleIFundi = async (trainingId, isCritical) => {
    const previousValue =
      gridData.find((row) => row.training_id === trainingId)?.is_critical ??
      false;

    // Optimistic update - update UI immediately
    setGridData((prevData) =>
      prevData.map((row) =>
        row.training_id === trainingId
          ? { ...row, is_critical: isCritical }
          : row
      )
    );

    try {
      const { data: existing, error: fetchError } = await supabase
        .from("training_catalogue_data_types")
        .select("id")
        .eq("training_id", trainingId)
        .single();

      if (fetchError && fetchError.code !== "PGRST116") {
        // Revert UI on database error
        setGridData((prevData) =>
          prevData.map((row) =>
            row.training_id === trainingId
              ? { ...row, is_critical: previousValue }
              : row
          )
        );
        console.error("Error fetching existing record:", fetchError);
        toast.error(`Failed to update: ${fetchError.message}`);
        return;
      }

      if (existing && existing.id) {
        await dispatch(
          updateTrainingIsCriticalLog({
            id: existing.id,
            companyId: localUser.company_id,
            isCritical: isCritical,
            trainingId: trainingId,
          })
        ).unwrap();
      } else {
        await dispatch(
          addTrainingIsCriticalLog({
            companyId: localUser.company_id,
            isCritical: isCritical,
            trainingId: trainingId,
          })
        ).unwrap();
      }

      // Update vector database (non-critical, but log if fails)
      const vectorUpdateResult = await updateTrainingInVectorDB(trainingId, {
        is_critical: isCritical,
      });
      if (!vectorUpdateResult.success) {
        console.warn(
          "Vector database update failed (non-critical):",
          vectorUpdateResult.error
        );
        // Don't revert UI for vector DB failures as it's non-critical
      }

      toast.success("Is Critical status updated successfully");
    } catch (error) {
      // Revert UI on any unexpected error
      setGridData((prevData) =>
        prevData.map((row) =>
          row.training_id === trainingId
            ? { ...row, is_critical: previousValue }
            : row
        )
      );
      console.error("Error updating is_critical:", error);
      toast.error(
        `Error updating is_critical: ${
          error.message || "An unexpected error occurred"
        }`
      );
    }
  };

  // Trigger resize event when sidebar toggles to recalculate grid columns
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 100);
    return () => clearTimeout(timer);
  }, [isCompanyOpen]);

  return (
    <div className="h-[calc(100vh-116px)] relative flex flex-col overflow-hidden">
      <h2 className="2xl:text-3xl 2xl:leading-3xl text-2xl leading-2xl text-blue-500 font-semibold 2xl:mb-4 md:mb-3 mb-4 flex-shrink-0">
        iFundi Catalogue
      </h2>
      <div
        className={`flex flex-1 min-h-0 ${isCompanyOpen ? "gap-4" : "gap-0"}`}
      >
        <div
          className={`bg-white border border-blue-200 rounded-xl flex flex-col overflow-hidden ${
            isCompanyOpen
              ? "opacity-100 w-[calc(30%-8px)] p-4 min-w-0 h-full min-h-0"
              : "opacity-0 w-0 min-w-0 p-0 h-0 border-0 pointer-events-none"
          }`}
        >
          <div className="flex items-center justify-between">
            <h3 className="!font-[Poppins] text-lg font-semibold text-blue-500 ">
              Catalogue List
            </h3>
            <CButton
              onClick={() => setIsCompanyOpen(!isCompanyOpen)}
              className={`!ml-auto !w-auto !h-auto !p-0 ${
                isCompanyOpen ? "" : "rotate-180"
              }`}
            >
              <ToggleBarIcon
                className={`w-[24px] h-[24px] !stroke-blue-500 `}
              />
            </CButton>
          </div>
          <div className="flex-1 overflow-y-auto mt-4">
            {filteredCategories?.length > 0 ? (
              [...filteredCategories]
                .sort((a, b) =>
                  a.name.localeCompare(b.name, undefined, {
                    sensitivity: "base",
                  })
                )
                .map((category, index) => (
                  <div
                    key={category.id}
                    className="flex justify-between items-center border-b border-solid border-blue-100 py-3"
                  >
                    <span
                      className={`line-clamp-1 text-sm leading-sm font-medium cursor-pointer ${
                        selectedIFundiCategory?.id === category.id
                          ? "text-blue-700 font-semibold"
                          : "text-gray-700 hover:text-blue-500"
                      }`}
                      onClick={() => handleIFundiCategoryClick(category, index)}
                    >
                      {category.name}
                    </span>
                    {userRole === roleType.ADMIN && (
                      <CSwitchToggle
                        className="progress-switch-btn"
                        checked={category.status}
                        onChange={() =>
                          handleStatusToggle(category.id, category.status)
                        }
                      />
                    )}
                  </div>
                ))
            ) : (
              <div className="text-center py-4 text-gray-500">
                No catalogues found
              </div>
            )}
          </div>
          {userRole === roleType.ADMIN && (
            <div className="w-full 2xl:p-3 p-2 text-right border-t border-blue-100">
              <CButton
                className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700"
                onClick={() => setDialogState({ id: null, show: true })}
              >
                Add Catalogue
              </CButton>
            </div>
          )}
          {dialogState.show && (
            <CDialog
              open={dialogState.show}
              onClose={handleDialogClose}
              className="bg-[#95AEBB80]"
            >
              <div className="min-w-[500px] p-3">
                <div className="flex items-center justify-between">
                  <p className="2xl:text-xl 2xl:leading-xl text-base leading-base text-grey-700 font-semibold mb-4">
                    Add Catalogue
                  </p>
                  <CButton
                    className="!h-auto !p-0 text-blue-500 font-semibold !ml-auto !underline absolute right-[16px] top-[16px]"
                    onClick={handleDialogClose}
                  >
                    <GridCloseIcon className="text-gray-700" />
                  </CButton>
                </div>
                <div className="2xl:my-3 my-2 w-full">
                  <CTextField
                    name="trainingName"
                    label="Training name"
                    className="w-full"
                    value={trainingName}
                    onChange={(e) => setTrainingName(e.target.value)}
                    placeholder={TRAINING_CATALOGUE_PLACEHOLDERS.catalogueName}
                  />
                </div>
                <div className="2xl:my-3 my-2 w-full">
                  <label className="mb-[6px] block 2xl:text-base 2xl:leading-base text-sm leading-sm text-gray-700">
                    Upload Training Catalogue
                  </label>
                  <div className="flex flex-col gap-2">
                    <label className="cursor-pointer 2xl:text-base 2xl:leading-base text-sm leading-sm inline-flex items-center justify-center gap-1 2xl:h-[50px] h-[45px] rounded-[8px] 2xl:px-6 px-4 py-3 bg-blue-500 hover:bg-blue-700 text-white w-full">
                      <UploadIcon className="text-white" /> Upload Training
                      Catalogue
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        hidden
                        onChange={handleFileUpload}
                      />
                    </label>
                    {selectedFileName && (
                      <div className="flex items-center justify-between bg-blue-50 border border-blue-100 px-4 py-2 rounded-md">
                        <div className="flex items-center gap-2">
                          <UploadIcon className="text-blue-500" />
                          <p className="text-sm text-blue-700 font-medium truncate">
                            {selectedFileName}
                          </p>
                        </div>
                        <CButton
                          onClick={handleCancelUpload}
                          className="!min-w-0 !p-1 hover:bg-blue-100 rounded-full"
                        >
                          <CloseIcon className="text-blue-500" />
                        </CButton>
                      </div>
                    )}
                  </div>
                </div>
                <CButton
                  onClick={handleSave}
                  disabled={!trainingName.trim() || isSaving}
                  className={`mt-5 bg-blue-500 hover:bg-blue-700 text-white border border-solid border-blue-500 hover:border-blue-700 ${
                    isSaving ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSaving ? "Saving..." : "Save"}
                </CButton>
              </div>
            </CDialog>
          )}
        </div>
        <div
          className={`${
            isCompanyOpen ? "w-[calc(70%-8px)]" : "w-full"
          } flex-1 min-w-0`}
        >
          <div className="bg-white border border-solid border-blue-200 rounded-xl overflow-hidden flex flex-col h-full min-h-0">
            {isCompanyOpen && !isCompanyPrivileged(userRole) && (
              <div className="flex items-center p-4 pt-6 pb-0">
                <div className="w-full">
                  <div className="border-2 border-blue-500 bg-white rounded-lg p-4 mb-0 relative w-full">
                    <p className="bg-white text-lg font-semibold text-gray-700 mb-4 2xl:text-xl 2xl:leading-xl lg:text-base lg:leading-base text-sm leading-sm absolute top-[-16px] left-[16px] px-1">
                      Instructions
                    </p>
                    <div>
                      <p className="font-medium text-sm  leading-sm text-gray-300">
                        Please upload the Training Catalogue in the required
                        format. You can download the template by clicking the
                        button below. &nbsp; &nbsp;
                        <CButton
                          className="text-blue-600 underline font-semibold 2xl:!h-auto !h-auto 2xl:!p-0 !p-0 !text-sm !leading-sm"
                          onClick={downloadTrainingCatalogueTemplate}
                        >
                          Click Here
                        </CButton>
                        &nbsp; to download the template. Once completed, upload
                        your file to proceed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              className={`flex items-center ${
                isCompanyOpen ? "justify-between" : "gap-3"
              } p-3`}
            >
              {!isCompanyOpen && (
                <CButton
                  onClick={() => setIsCompanyOpen(!isCompanyOpen)}
                  className="!w-auto !h-auto rotate-180"
                >
                  <ToggleBarIcon
                    className={`w-[24px] h-[24px] !stroke-blue-500 `}
                  />
                </CButton>
              )}
              <CTextField
                name="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startIcon={<SearchIcon className="text-blue-300" />}
                placeholder={"Search"}
                className={`!mt-0 ${
                  isCompanyOpen
                    ? "w-full 2xl:max-w-[400px] max-w-[300px]"
                    : "flex-1"
                }`}
              />
              <div className="flex items-center gap-3">
                {userRole === roleType.ADMIN && (
                  <CButton
                    onClick={() => {
                      if (!selectedIFundiCategory) {
                        toast.warning(messages.SELECT_TRAINING_CATEGORY);
                        return;
                      }

                      navigate("/add-training-catalogue", {
                        state: {
                          categoryId: selectedIFundiCategory.id,
                          activeTab: "IFundiCatalogue",
                        },
                      });
                    }}
                    className="text-white bg-blue-500 hover:bg-blue-700 border border-solid border-blue-500 hover:border-blue-700"
                  >
                    Add Training
                  </CButton>
                )}
                <CButton
                  onClick={handleExport}
                  className="text-white gradient-btn rounded-lg p-0"
                >
                  Export All
                </CButton>
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <CDataGrid
                key={`grid-${isCompanyOpen}`}
                idKey="id"
                searchQuery={searchQuery}
                rows={gridData}
                columns={columns}
                pageSize={10}
                height="100%"
              />
            </div>
            {alertDialogState.show && (
              <ConfirmationDialog
                open={alertDialogState.show}
                onClose={() => setAlertDialogState({ id: null, show: false })}
                onConfirm={() => {
                  handleDelete(alertDialogState.id);
                  setAlertDialogState({ id: null, show: false });
                }}
                title="Delete Training"
                description="Are you sure you want to delete this training?"
                confirmText="Delete"
                cancelText="Cancel"
                confirmClass="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 border-blue-700 px-4 py-2 rounded-lg"
                cancelClass="bg-white text-blue-500 hover:text-white hover:bg-blue-500 border border-blue-500 px-4 py-2 rounded-lg"
              />
            )}
          </div>
        </div>
      </div>
      {/* Training Details Dialog */}
      <CDialog
        open={courseDetailsDialog.open}
        onClose={() =>
          setCourseDetailsDialog({ open: false, description: "", title: "" })
        }
        maxWidth="lg"
      >
        <div className="md:w-[900px] w-full flex flex-col max-h-[90vh]">
          {/* Header - Centered */}
          <div className="flex items-center justify-center p-6 pb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Training Details
            </h2>
          </div>

          {/* Content - Description shown directly */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <div className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap">
              {courseDetailsDialog.description || "No description available."}
            </div>
          </div>

          {/* Footer - Only Close button */}
          <div className="flex justify-center p-6 pt-4">
            <CButton
              className="bg-blue-500 hover:bg-blue-700 min-w-[100px] text-white border border-solid border-blue-500 hover:border-blue-700"
              onClick={() =>
                setCourseDetailsDialog({
                  open: false,
                  description: "",
                  title: "",
                })
              }
            >
              Close
            </CButton>
          </div>
        </div>
      </CDialog>

      {/* Validation Error Dialog */}
      <CDialog
        open={validationDialog.show}
        onClose={() => setValidationDialog({ show: false, errors: [] })}
        className="validation-dialog bg-[#95AEBB80]"
      >
        <div className="p-5">
          <p className="text-2xl leading-2xl text-grey-700 font-semibold text-center mb-4">
            Validation Errors
          </p>
          <div className="max-h-[400px] overflow-y-auto">
            {validationDialog.errors.map((error, index) => (
              <p key={index} className="text-gray-300 mb-2">
                {error}
              </p>
            ))}
          </div>
          <div className="flex justify-center mt-6">
            <CButton
              onClick={() => setValidationDialog({ show: false, errors: [] })}
              className="bg-blue-500 hover:bg-blue-700 min-w-[80px] text-white border border-solid border-blue-500 hover:border-blue-700"
            >
              Close
            </CButton>
          </div>
        </div>
      </CDialog>

      {catalogueStatusWarningDialog.show && (
        <ConfirmationDialog
          open={catalogueStatusWarningDialog.show}
          onClose={() =>
            setCatalogueStatusWarningDialog({
              show: false,
              categoryId: null,
              nextCatalogueActive: null,
            })
          }
          onConfirm={() =>
            setCatalogueStatusWarningDialog({
              show: false,
              categoryId: null,
              nextCatalogueActive: null,
            })
          }
          title="Catalogue has allocated trainings"
          description="Some trainings in this catalogue are still selected in employee training plans. This catalogue cannot be made unavailable while it contains active allocations."
          confirmText="OK"
          cancelText={null}
          confirmClass="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 border-blue-700 px-4 py-2 rounded-lg"
        />
      )}

      {trainingStatusWarningDialog.show && (
        <ConfirmationDialog
          open={trainingStatusWarningDialog.show}
          onClose={() =>
            setTrainingStatusWarningDialog({
              show: false,
              trainingId: null,
              trainingName: "",
            })
          }
          onConfirm={() =>
            setTrainingStatusWarningDialog({
              show: false,
              trainingId: null,
              trainingName: "",
            })
          }
          title="Training already allocated"
          description={`"${trainingStatusWarningDialog.trainingName}" is already assigned to employees and cannot be updated.`}
          confirmText="OK"
          cancelText={null}
          confirmClass="bg-blue-500 text-white hover:bg-blue-700 border border-blue-500 border-blue-700 px-4 py-2 rounded-lg"
        />
      )}
    </div>
  );
};

export default IFundiCatalogue;
